import { useEffect, useState } from "react";

import {
  getDgBannerApi,
  hideConsentBanner,
} from "@/datagrailIntegration/dgBannerApi";

import isCrossOriginEmbedded from "../isCrossOriginEmbedded";

export type StorageAccessStatus =
  | "not-needed"
  | "unsupported"
  | "pending"
  | "granted"
  | "denied";

const GESTURE_EVENTS = ["pointerdown", "keydown"] as const;

/**
 * Recovers DataGrail's consent cookie where third-party storage is blocked outright.
 *
 * The partitioned-cookie shim covers Chrome, Edge and Firefox. Safari ignores
 * `Partitioned` and blocks third-party cookies, so the only route to a
 * persistent consent record there is the Storage Access API. That API needs
 * transient activation, which is why this waits for a gesture instead of asking
 * on mount.
 *
 * A denial is an ordinary outcome, not an error. Safari refuses unless the user
 * has visited newsela.com as a first-party site recently, which a student
 * reaching us only through Canvas never has. When that happens the banner keeps
 * appearing once per page load, which the block-reload script already made
 * survivable.
 */
const useStorageAccess = (): StorageAccessStatus => {
  const [status, setStatus] = useState<StorageAccessStatus>("pending");

  useEffect(() => {
    if (!isCrossOriginEmbedded()) {
      setStatus("not-needed");
      return;
    }

    if (
      !("hasStorageAccess" in document) ||
      !("requestStorageAccess" in document)
    ) {
      setStatus("unsupported");
      return;
    }

    let cancelled = false;
    let requested = false;

    const removeListeners = () => {
      GESTURE_EVENTS.forEach((event) =>
        document.removeEventListener(event, onGesture),
      );
    };

    // Access is available. DataGrail reads the cookie live on every call, so a
    // stored choice can be honoured now even though the banner already rendered.
    const settle = () => {
      if (cancelled) {
        return;
      }
      setStatus("granted");
      if (getDgBannerApi()?.getConsentPreferences().isCustomised) {
        hideConsentBanner();
      }
    };

    function onGesture() {
      if (requested) {
        return;
      }
      requested = true;
      removeListeners();

      document.requestStorageAccess().then(settle, () => {
        if (!cancelled) {
          setStatus("denied");
        }
      });
    }

    document.hasStorageAccess().then(
      (hasAccess) => {
        if (cancelled) {
          return;
        }
        if (hasAccess) {
          settle();
          return;
        }
        GESTURE_EVENTS.forEach((event) =>
          document.addEventListener(event, onGesture),
        );
      },
      () => {
        if (!cancelled) {
          setStatus("denied");
        }
      },
    );

    return () => {
      cancelled = true;
      removeListeners();
    };
  }, []);

  return status;
};

export default useStorageAccess;
